import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Table } from 'src/design/molecules/Table';
import React from 'react';
import { WbsAdjustmentDto } from 'src/api/FiretrolApiClient';
import {
  logger,
  showErrorToast,
  showSuccessToast,
  formatDate,
} from 'src/utils';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';

interface AdjustmentsTableProps {
  onAdjustmentSelect: (adjustment: WbsAdjustmentDto | null) => void;
}

export const AdjustmentsTable: React.FC<AdjustmentsTableProps> = ({
  onAdjustmentSelect,
}) => {
  const {
    selectedTreeRow,
    adjustments,
    selectedAdjustment,
    adjustmentsLoading,
    adjustmentsError,
    deleteAdjustment,
  } = useWbs();

  if (!selectedTreeRow) {
    return null;
  }

  const handleDeleteAdjustment = async (adjustment: WbsAdjustmentDto) => {
    try {
      await deleteAdjustment(adjustment.id);
      showSuccessToast('Adjustment deleted successfully!');
    } catch (err) {
      logger.error('Failed to delete adjustment', { err });
      showErrorToast('Failed to delete adjustment');
    }
  };

  if (selectedTreeRow.isArtificial) {
    return (
      <Alert color="blue" title="Information">
        Adjustments can only be viewed for charge codes, not roll-up accounts.
      </Alert>
    );
  }

  if (adjustmentsLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (adjustmentsError) {
    return (
      <Alert color="red" title="Error">
        {adjustmentsError}
      </Alert>
    );
  }

  const isLaborAccount = selectedTreeRow.accountType === 'labor';

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={5}>Adjustments History</Title>
        <Text size="sm" c="dimmed">
          {adjustments.length} adjustment{adjustments.length !== 1 ? 's' : ''}
        </Text>
      </Group>

      {adjustments.length === 0 ? (
        <Paper p="md" ta="center">
          <Text c="dimmed">No adjustments found for this account.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Description</Table.Th>
                {isLaborAccount && <Table.Th>Target</Table.Th>}
                <Table.Th>Created By</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {adjustments.map((adjustment) => (
                <Table.Tr
                  key={adjustment.id}
                  style={{
                    backgroundColor:
                      selectedAdjustment?.id === adjustment.id
                        ? 'var(--mantine-color-blue-0)'
                        : undefined,
                  }}
                >
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {adjustment.adjustmentType?.name || 'Unknown Type'}
                      </Text>
                      {adjustment.isSystemGenerated && (
                        <Badge size="xs" color="gray" variant="light">
                          System
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      size="sm"
                      fw={500}
                      c={adjustment.amount >= 0 ? 'green' : 'red'}
                    >
                      {adjustment.amount >= 0 ? '+' : ''}
                      {adjustment.amount}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {formatDate(adjustment.adjustmentDate)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" style={{ maxWidth: 200 }}>
                      {adjustment.description || '-'}
                    </Text>
                  </Table.Td>
                  {isLaborAccount && (
                    <Table.Td>
                      <Text size="sm">{adjustment.target}</Text>
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Text size="sm">
                      {adjustment.createdBy?.fullName || 'Unknown'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {!adjustment.isSystemGenerated && (
                        <>
                          <EditActionIcon
                            size="sm"
                            onClick={() => onAdjustmentSelect(adjustment)}
                          />
                          <DeleteActionIcon
                            size="sm"
                            requiredConfirmation={true}
                            onDelete={() => handleDeleteAdjustment(adjustment)}
                          />
                        </>
                      )}
                      {adjustment.isSystemGenerated && (
                        <ViewActionIcon
                          size="sm"
                          onClick={() => onAdjustmentSelect(adjustment)}
                        />
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
};
